import React, { useEffect, useState } from 'react';
import { Button, Modal, message, Form, Input, Select,  } from 'antd';
import { DataMapApi, DatasourceType } from '@/api/dataMapApi';
import { DataSourceType } from '../../hooks/useInitDataMap';
import { DataSourceIcon, NewDataSourceIcon } from '../icons';

interface AddSystemModalProps {
  visible: boolean;
  onClose: () => void;
  onSystemAdded: (system: DataSourceType) => void;
  // currentSystemId?: string | null;
}

interface FormData {
  name: string;
  datasourceType: DatasourceType;
  databaseType: string;
  host: string;
  port: string;
  username: string;
  password: string;
  initDatabase: string;
}

const AddSystemModal: React.FC<AddSystemModalProps> = ({ visible, onClose, onSystemAdded }) => {
  // 数据源类型选择状态
  const [selectedDataSourceType, setSelectedDataSourceType] = useState('no-data-source'); // no-data-source, existing-data-source, new-data-source
  // 选择的数据源
  const [selectedDataSource, setSelectedDataSource] = useState<string | null>(null);
  // 表单实例
  const [form] = Form.useForm<FormData>();
  // 本地文件数据源名称
  const [localDataSourceName, setLocalDataSourceName] = useState('');
  
  // 当模态框打开且currentSystemId为local-node时，默认选择本地文件上传
  // useEffect(() => {
  //   if (visible && currentSystemId === 'local-node') {
  //     setSelectedDataSourceType('no-data-source');
  //   } else if (visible && currentSystemId !== 'local-node') {
  //     setSelectedDataSourceType('new-data-source');
  //   }
  // }, [visible, currentSystemId]);

  // 初始化表单默认值 - 每次打开模态框时重置
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);



  // 处理数据源类型选择
  const handleDataSourceTypeSelect = (type: string) => {
    setSelectedDataSourceType(type);
    // 重置相关状态
    if (type !== 'existing-data-source') {
      setSelectedDataSource(null);
    }
    if (type === 'new-data-source') {
      const initialValues = {
        name: '',
        host: '',
        port: '',
        username: '',
        password: '',
        databaseType: '',
        initDatabase: '',
      };
      // 使用form实例设置表单值
      form.setFieldsValue(initialValues);
    }
  };

  // 处理数据源选择
  const handleDataSourceSelect = (dataSourceId: string) => {
    setSelectedDataSource(dataSourceId);
  };

  // 完成添加系统
  const handleCompleteAddSystem = async () => {
    let newSystem: DataSourceType | undefined = undefined;
    
    if (selectedDataSourceType === 'no-data-source' && localDataSourceName) {
      // 本地文件数据源
      try {
        // 验证数据源名称，限制特殊字符
        const nameRegex = /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/;
        if (!nameRegex.test(localDataSourceName)) {
          message.error('数据源名称只能包含字母、数字、下划线和中文');
          return;
        }
        
        // 创建数据源
        const res = await DataMapApi.createRelateDataSource({
          name: localDataSourceName, 
          datasourceType: DatasourceType.FILES,
        });
        if(!!res?.data?.id){
          newSystem = {
            id: res.data.id,
            name: localDataSourceName,
            datasourceType: DatasourceType.FILES,
            tables: []
          };
          message.success('数据源创建成功');
        } else {
          message.error('创建数据源失败');
          return;
        }
      } catch (error) {
        message.error('创建数据源失败');
        console.error('创建数据源错误:', error);
        return;
      }
    } else if (selectedDataSourceType === 'new-data-source') {
      // 新建数据源
      try {
        const formValues = await form.validateFields();
        const res = await DataMapApi.createRelateDataSource({
          ...formValues,
          datasourceType: DatasourceType.RELATIONSHIP,
        });

        if (!!res && res.data && res.data.id) {
          newSystem = {
            ...res.data,
            tables: []
          };
        } else {
          message.error('创建数据源失败');
          return;
        }
      } catch (error: any) {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstError = error.errorFields[0];
          message.error(firstError.errors[0] || '表单验证失败，请检查输入');
        } else {
          message.error('表单验证失败，请检查输入');
        }
        console.error('表单验证错误:', error);
        return;
      }
    } else {
      // 验证失败
      message.error('请完成必要的选择');
      return;
    }
    
    if (newSystem) {
      onSystemAdded(newSystem);
      handleClose();
    }
  };

  // 关闭模态框并重置状态
  const handleClose = () => {
    // 重置表单
    form.resetFields();
    // 重置其他状态
    setSelectedDataSourceType('no-data-source');
    setSelectedDataSource(null);
    setLocalDataSourceName('');
    onClose();
  };

  return (
    <Modal
      title="添加系统"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      centered
      forceRender
      style={{ zIndex: 1001 }}
    >
      <div className="py-8 px-12">
        {/* 数据源类型选择 */}
        <div className="flex justify-center gap-8 mb-8">
          {/* 无数据源 - 只在本地节点添加表时显示 */}
          <div 
            className={`border-2 ${selectedDataSourceType === 'no-data-source' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'} rounded-lg p-6 w-64 cursor-pointer`}
            onClick={() => handleDataSourceTypeSelect('no-data-source')}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`mb-4 ${selectedDataSourceType === 'no-data-source' ? 'text-blue-500' : 'text-gray-400'}`}>
                <DataSourceIcon isActive={selectedDataSourceType === 'no-data-source'} />
              </div>
              <h4 className={`font-medium mb-1 ${selectedDataSourceType === 'no-data-source' ? 'text-[#132347]' : 'text-gray-500'}`}>
                本地文件
              </h4>
              <p className="text-sm text-gray-500">上传本地文件</p>
            </div>
          </div>
          
          {/* 新建数据源 - 只在点击添加系统时显示 */}
          <div 
            className={`border-2 ${selectedDataSourceType === 'new-data-source' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'} rounded-lg p-6 w-64 cursor-pointer`}
            onClick={() => handleDataSourceTypeSelect('new-data-source')}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`mb-4 ${selectedDataSourceType === 'new-data-source' ? 'text-blue-500' : 'text-gray-400'}`}>
                <NewDataSourceIcon />
              </div>
              <h4 className={`font-medium mb-1 ${selectedDataSourceType === 'new-data-source' ? 'text-[#132347]' : 'text-gray-500'}`}>新建数据源</h4>
              <p className="text-sm text-gray-500">选择数据表</p>
            </div>
          </div>
        </div>
        
        {/* 根据选择的数据源类型显示不同内容 */}
        {selectedDataSourceType === 'no-data-source' && (
          <div>
            <h3 className="text-lg font-medium mb-6">本地文件数据源</h3>
            
            {/* 数据源名称输入 */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">数据源名称</label>
              <Input 
                value={localDataSourceName}
                onChange={(e) => setLocalDataSourceName(e.target.value)}
                placeholder="请输入数据源名称"
              />
            </div>
            
            <div className="flex justify-end">
              <Button 
                type="primary"
                onClick={handleCompleteAddSystem}
                disabled={!localDataSourceName}
              >
                完成
              </Button>
            </div>
          </div>
        )}
        
        {/* 数据源绑定远程数据库选项 - 只在点击添加系统时显示 */}
        {selectedDataSourceType === 'new-data-source' && (
          <div>
            <h3 className="text-lg font-medium mb-6">新建数据源</h3>
            
            <Form
              layout="vertical"
              form={form}
              onFinish={() => {
                handleCompleteAddSystem();
              }}
            >
              {/* 数据源名称 */}
              <Form.Item
                label="数据源名称"
                name="name"
                rules={[
                  { required: true, message: '请输入数据源名称' },
                  { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/, message: '数据源名称只能包含字母、数字、下划线和中文' }
                ]}
                className="mb-4"
              >
                <Input placeholder="请输入数据源名称" />
              </Form.Item>
              
              {/* 数据源地址 */}
              <Form.Item
                label="数据源地址"
                name="host"
                rules={[{ required: true, message: '请输入数据源地址' }]}
                className="mb-4"
              >
                <Input placeholder="请输入数据源地址" />
              </Form.Item>
              {/* 数据源端口 */}
              <Form.Item
                label="数据源端口"
                name="port"
                rules={[
                  { required: true, message: '请输入数据源端口' },
                  { pattern: /^[0-9]+$/, message: '请输入有效的端口号' }
                ]}
                className="mb-4"
              >
                <Input placeholder="请输入数据源端口" />
              </Form.Item>
              {/* 用户名 */}
              <Form.Item
                label="用户名"
                name="username"
                className="mb-4"
              >
                <Input placeholder="请输入用户名" autoComplete="off" />
              </Form.Item>
              
              {/* 密码 */}
              <Form.Item
                label="密码"
                name="password"
                className="mb-4"
              >
                <Input.Password placeholder="请输入密码" autoComplete="new-password" />
              </Form.Item>
              {/* 数据库类型 */}
              <Form.Item
                label="数据库类型"
                name="databaseType"
                rules={[{ required: true, message: '请选择数据库类型' }]}
                className="mb-6"
              >
                <Select>
                  <Select.Option value="">请选择</Select.Option>
                  <Select.Option value="MySQL">MySQL</Select.Option>
                  <Select.Option value="PostgreSQL">PostgreSQL</Select.Option>
                </Select>
              </Form.Item>
              
              {/* 连接数据库名称 */}
              <Form.Item
                label="连接数据库名称"
                name="initDatabase"
                rules={[{ required: true, message: '请输入连接数据库名称' }]}
                className="mb-4"
              >
                <Input placeholder="连接数据库名称" />
              </Form.Item>
              {/* 表单提交按钮 */}
              <Form.Item className="mb-0 text-end">
                <Button 
                  type="primary"
                  htmlType="submit"
                >
                  完成
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddSystemModal;